<template>
  <div class="tiktok-post-filters" layout="column">
    <div class="mb-x1">
      <div class="mb-x1">応募期間</div>
      <AppDateRangePicker
        :value="entryDateRange"
        :campaign="campaign"
        @change="onChangeEntryDateRange"
        :placeholder="['開始日時', '終了日時']"
        size="small"
      />
    </div>
    <div class="mb-x3" layout="row">
      <div>
        <div class="mb-x1">応募グループ</div>
        <el-select
          v-model="prizeId"
          size="small"
          placeholder="応募グループ"
          clearable
          @change="onChangeFilter('prizeId')"
        >
          <el-option v-for="item in prizeOptions" :key="item.id" :label="item.name" :value="item.id"></el-option>
        </el-select>
      </div>
      <div class="ml-x1">
        <div class="mb-x1">候補者</div>
        <el-select
          v-model="isCandidate"
          size="small"
          placeholder="候補者"
          clearable
          @change="onChangeFilter('isCandidate')"
        >
          <el-option
            v-for="(item, index) in isCandidateOptions"
            :key="index"
            :label="item.label"
            :value="item.value"
          ></el-option>
        </el-select>
      </div>
      <div class="ml-x1">
        <div class="mb-x1">当選者</div>
        <el-select v-model="isWinner" size="small" placeholder="当選者" clearable @change="onChangeFilter('isWinner')">
          <el-option
            v-for="(item, index) in isWinnerOptions"
            :key="index"
            :label="item.label"
            :value="item.value"
          ></el-option>
        </el-select>
      </div>
      <div class="ml-x1">
        <div class="mb-x1">アカウント名</div>
        <el-input
          v-model="accountName"
          type="text"
          size="small"
          placeholder="検索(完全一致)"
          @change="onChangeFilter('accountName')"
          clearable
        >
          <template slot="prepend"><i class="mdi mdi-magnify"></i></template>
        </el-input>
      </div>
    </div>
    <div class="mb-x0" layout="row">
      <div class="mb-x0">
        <el-button type="primary" @click="changeFilter" :disabled="loading"
          ><i class="mdi mdi-magnify"></i>検索</el-button
        >
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { mapActions, mapState } from 'pinia'
import { defineComponent } from 'vue'

import { AppDateRangePicker } from '@/client/components/_atoms'
import { TrackingService } from '@/client/services'
import { useTikTokPostsStore } from '@/client/store/useTikTokPostsStore'
import { APIGetTikTokPostsParams } from '@/typings/api/tiktok_posts.type'

export default defineComponent({
  components: {
    AppDateRangePicker
  },
  created() {
    this.prizeId = this.params.prizeId
    this.isCandidate = this.params.isCandidate
    this.isWinner = this.params.isWinner
    this.accountName = this.params.accountName

    // 既存のパラメータがある場合はそれを使用
    if (this.params.entryDateFrom || this.params.entryDateTo) {
      this.entryDateRange = [this.params.entryDateFrom || '', this.params.entryDateTo || '']
    } else {
      // デフォルト値は空にしておく（AppDateRangePickerで設定）
      this.entryDateRange = ['', '']
    }
  },
  data: () => {
    return {
      prizeId: '' as APIGetTikTokPostsParams['prizeId'],
      isCandidate: '' as APIGetTikTokPostsParams['isCandidate'],
      isWinner: '' as APIGetTikTokPostsParams['isWinner'],
      accountName: '',
      entryDateRange: ['', ''],
      isCandidateOptions: [
        { value: '', label: '指定なし' },
        { value: 1, label: '候補者フラグあり' },
        { value: 0, label: '候補者フラグなし' }
      ],
      isWinnerOptions: [
        { value: '', label: '指定なし' },
        { value: 1, label: '当選フラグあり' },
        { value: 0, label: '当選フラグなし' }
      ]
    }
  },
  computed: {
    ...mapState(useTikTokPostsStore, {
      params: state => state.params,
      prizes: state => state.prizes,
      campaign: state => state.campaign,
      loading: state => state.loading
    }),

    prizeOptions() {
      return [{ id: '', name: '指定なし' }, ...this.prizes]
    }
  },
  methods: {
    ...mapActions(useTikTokPostsStore, ['setParams']),

    onChangeFilter(value: string) {
      switch (value) {
        case 'prizeId':
          TrackingService.sendEvent('select:応募履歴（TT）|応募グループ')
          break
        case 'isCandidate':
          TrackingService.sendEvent('select:応募履歴（TT）|候補者')
          break
        case 'isWinner':
          TrackingService.sendEvent('select:応募履歴（TT）|当選者')
          break
        case 'accountName':
          TrackingService.sendEvent('select:応募履歴（TT）|アカウント名')
          break
        case 'entryDateRange':
          TrackingService.sendEvent('select:応募履歴（TT）|応募期間')
          break
        default:
          break
      }
    },

    onChangeEntryDateRange(value) {
      TrackingService.sendEvent('select:応募履歴（TT）|応募期間')
      this.entryDateRange = value || ['', '']
    },

    async changeFilter() {
      TrackingService.sendEvent('click:応募履歴（TT）|検索')

      const params: any = {
        prizeId: this.prizeId,
        isCandidate: this.isCandidate,
        isWinner: this.isWinner,
        accountName: this.accountName
      }

      // 応募期間を追加
      if (this.entryDateRange[0]) {
        params.entryDateFrom = this.entryDateRange[0]
      }
      if (this.entryDateRange[1]) {
        params.entryDateTo = this.entryDateRange[1]
      }

      await this.setParams(params)
    }
  }
})
</script>
<style lang="scss" scoped>
@use '@/client/assets/scss/variables.scss' as *;

.account-name {
  width: $spacing-unit * 36;
}
</style>
